import React from 'react';
import { htmlDecode } from '../helpers/functions';
import cx from 'classnames';
import { Field } from 'react-final-form';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import DatePicker from 'react-date-picker';

import { motion, AnimatePresence } from 'framer-motion';

import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

export const required = (value) => (value ? undefined : 'Required');

const normalizePhone = (value) => {
  if (!value) return value;
  const onlyNums = value.replace(/[^\d]/g, '');
  if (onlyNums.length <= 3) return onlyNums;
  if (onlyNums.length <= 7)
    return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 7)}`;
  return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(
    6,
    10
  )}`;
};

const validZipForm = (value) => {
  if (!value) return value;
  const onlyNums = value.replace(/[^\d]/g, '');
  return onlyNums.slice(0, 5);
};

function handleParseType(type, value) {
  if (type === 'phone') {
    return normalizePhone(value);
  }
  if (type === 'postcode') {
    return validZipForm(value);
  }
  return value;
}

export const ButtonSubmit = ({ text, errors, ...props }) => (
  <button
    type="submit"
    className="disabled:bg-gray-light mt-4 disabled:text-gray focus:outline-none inline-block bg-primary transition duration-200 hover:bg-primary-dark text-content px-12 py-4 uppercase font-bold"
    disabled={errors.validate ? true : false}
  >
    {text}
  </button>
);

export const ErrorDisplay = ({ message, ...props }) => (
  <div className="p-8 bg-secondary text-primary font-bold">{message}</div>
);

const inputStyle = `w-full focus:outline-none bg-white rounded-lg focus:border-transparent px-4 py-6 pb-2`;

export const FancyInput = ({ field, ...props }) => {
  return (
    <Field
      name={`input_${field.id}`}
      type={'text'}
      validate={field.required ? required : false}
      parse={(value) => {
        return handleParseType(field.type, value);
      }}
    >
      {({ input, meta }) => (
        <div className="relative">
          <motion.label
            animate={{ opacity: 1, y: -10 }}
            initial={{ opacity: 0, y: 15 }}
            exit={{ opacity: 0, y: 15 }}
            className="text-14 absolute top-4 left-3 text-gray-medium"
          >
            {`${field.label}${field.required ? `*` : ``}`}
          </motion.label>
          <input
            {...input}
            className={cx(
              inputStyle,
              meta.touched && meta.error ? `border-red` : `border-gray-light`
            )}
          />
        </div>
      )}
    </Field>
  );
};

export const FancyHidden = ({ field, ...props }) => (
  <Field
    name={field.name}
    type={'hidden'}
    validate={field.required ? required : false}
    initialValue={field.value ? field.value : ``}
    className="col-span-12"
    component={'input'}
  />
);
export const FancyPassword = ({ field, ...props }) => (
  <Field
    name={field.name}
    type={'password'}
    validate={field.required ? required : false}
    {...props}
  >
    {({ input, meta }) => (
      <div>
        <AnimatePresence>
          {meta.active || meta.valid ? (
            <motion.label
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 15 }}
              exit={{ opacity: 0, y: 15 }}
              className="text-xs absolute top-4 left-3 text-gray-medium"
            >
              {field.label}
            </motion.label>
          ) : (
            ``
          )}
        </AnimatePresence>
        <input
          {...input}
          placeholder={!meta.active ? field.label : ``}
          className={cx(
            inputStyle,
            meta.touched && meta.error ? `border-red` : `border-gray-light`,
            meta.active || meta.valid ? `py-6 pb-2` : `py-4`
          )}
        />
      </div>
    )}
  </Field>
);

export const FancyCheckbox = ({ field }) => (
  <Field
    name={field.name}
    type={'checkbox'}
    initialValue={field.value ? field.value : ``}
  >
    {({ input }) => (
      <div>
        <label className="flex items-center cursor-pointer">
          <input {...input} className="mr-2" />
          {field.label}
        </label>
      </div>
    )}
  </Field>
);

export const FancyRadio = ({ field }) => (
  <Field name={field.name} type={'radio'} value={field.value}>
    {({ input }) => (
      <div>
        <label className="flex items-center">
          <input {...input} className="mr-2" />
          {field.label}
        </label>
      </div>
    )}
  </Field>
);

export const FancySelect = ({ field, ...props }) => (
  <Field
    name={`input_${field.id}`}
    validate={field.required ? required : false}
    options={field.options}
    defaultLabel={field.label}
    {...props}
    defaultValue={false}
  >
    {({ input, options, defaultLabel, meta }) => (
      <div className="relative">
        <label className="text-xs absolute top-2 left-3 text-book">
          {field.label}
        </label>
        <select
          {...input}
          className={cx(
            `text-gray`,
            inputStyle,
            meta.touched && meta.error ? `border-red` : `border-gray-light`
          )}
          style={{ height: '58px' }}
        >
          <option disabled value={false}>
            {defaultLabel}
          </option>
          {options.map((option, key) => (
            <option key={key} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>
    )}
  </Field>
);

const Label = ({ text, ...props }) => (
  <label
    className="text-primary uppercase text-book text-sm"
    dangerouslySetInnerHTML={{ __html: htmlDecode(text) }}
    {...props}
  />
);

const FieldWrap = ({ children, ...props }) => <div {...props}>{children}</div>;

export const Error = ({ text, ...props }) => (
  <div
    className="p-2 bg-primary text-content text-sm"
    dangerouslySetInnerHTML={{ __html: htmlDecode(text) }}
  />
);

export const FancyTextArea = ({ field, ...props }) => {
  return (
    <Field
      name={`input_${field.id}`}
      type={'textarea'}
      validate={field.required ? required : false}
      parse={(value) => {
        return handleParseType(field.type, value);
      }}
    >
      {({ input, meta }) => (
        <div className="relative">
          <AnimatePresence>
            {meta.active ? (
              <motion.label
                animate={{ opacity: 1, y: -10 }}
                initial={{ opacity: 0, y: 15 }}
                exit={{ opacity: 0, y: 15 }}
                className="text-xs absolute top-4 left-3 text-gray-medium"
              >
                {field.label}
              </motion.label>
            ) : (
              ``
            )}
          </AnimatePresence>
          <textarea
            {...input}
            placeholder={!meta.active ? field.label : ``}
            rows="4"
            className={cx(
              inputStyle,
              meta.touched && meta.error ? `border-red` : `border-gray-light`,
              meta.active || meta.valid ? `py-6 pb-2` : `py-4`
            )}
          />
        </div>
      )}
    </Field>
  );
};
export const FancyEmail = ({ field, ...props }) => (
  <Field
    name={`input_${field.id}`}
    validate={(values) => {
      if (values) {
        if (!isEmail(values)) {
          return 'Enter valid email address';
        }
      } else {
        return 'Required';
      }
    }}
    type="text"
  >
    {({ input, meta }) => (
      <div className="relative">
        <AnimatePresence>
          <motion.label
            animate={{ opacity: 1, y: -10 }}
            initial={{ opacity: 0, y: 15 }}
            exit={{ opacity: 0, y: 15 }}
            className="text-xs absolute top-4 left-3 text-gray-medium"
          >
            {`${field.label}${field.required ? `*` : ``}`}
          </motion.label>
        </AnimatePresence>
        <input
          {...input}
          className={cx(
            inputStyle,
            meta.touched && meta.error ? `border-red` : `border-gray-light`
          )}
        />
      </div>
    )}
  </Field>
);

function getFormattedDate(date) {
  var year = date.getFullYear();

  var month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : '0' + month;

  var day = date.getDate().toString();
  day = day.length > 1 ? day : '0' + day;

  return month + '/' + day + '/' + year;
}

export const FancyDate = ({ field, form, ...props }) => {
  const [value, onChange] = React.useState(new Date());

  const [hasChanged, setHasChanged] = React.useState(false);

  React.useEffect(() => {
    if (!hasChanged) {
      form.mutators.setValue(`input_${field.id}`, getFormattedDate(new Date()));
      setHasChanged(true);
    }
  }, []);

  function handleDateChange(date) {
    onChange(date);
    form.mutators.setValue(`input_${field.id}`, getFormattedDate(date));
  }

  return (
    <div className="">
      <div className="flex space-x-8 items-center">
        <label className="text-18 font-bold">{field.label}</label>
        <DatePicker
          onChange={handleDateChange}
          value={value}
          format={'M/dd/yyyy'}
        />
      </div>
      <Field
        name={`input_${field.id}`}
        type={'hidden'}
        validate={field.required ? required : false}
        initialValue={value}
        className="col-span-12"
        component={'input'}
      />
    </div>
  );
};

export const FancyHtml = ({ field, ...props }) => (
  <div className="mt-8">
    <div dangerouslySetInnerHTML={{ __html: htmlDecode(field.content) }}></div>
  </div>
);

export const FancyAddress = ({ field, ...props }) => <div>Address</div>;

const fieldTypes = {
  text: FancyInput,
  email: FancyEmail,
  phone: FancyInput,
  select: FancySelect,
  radio: FancyRadio,
  checkbox: FancyCheckbox,
  password: FancyPassword,
  hidden: FancyHidden,
  textarea: FancyTextArea,
  address: FancyAddress,
  date: FancyDate,
  html: FancyHtml,
};

export function FormFields({ fields, form, ...props }) {
  return (
    <div {...props}>
      {fields && (
        <div className="flex flex-wrap -mx-2">
          {fields.map((field, key) => {
            const El = fieldTypes[field.type];
            return (
              <div className={cx(field.class, `w-full mb-4 px-4`)} key={key}>
                <El field={field} form={form} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
